<script>
	import { onDestroy, tick, onMount, createEventDispatcher } from "svelte";
	import { uiText } from "../lib/uiText.js";
	import {
		collection,
		query,
		orderBy,
		onSnapshot,
		addDoc,
		serverTimestamp,
		doc,
		getDoc,
		updateDoc,
		increment,
		limit,
	} from "firebase/firestore";
	import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

	const dispatch = createEventDispatcher();

	export let db;
	export let storage = null; // Optional if you want GM image uploads
	export let lang = "bg";
	export let team; // Passed from GameDisplay.svelte
	export let user;
	console.log(user);

	export let gid; // Game ID

	let ui = uiText(lang);
	$: lang, (ui = uiText(lang));

	let messagesArray = [];
	let newMessage = "";
	let chatContainer;
	let fileInput;
	let isUploading = false;
	let expandedImage = null;
	let textInput;

	let alias = false;
	let vendor = false;

	// Add these state variables near your other let declarations
	let messageLimit = 5;
	let unsubscribe = null;
	let isLoadingMore = false;

	// Define the references
	const sessionRef = doc(db, "sessions", team.session.sid);
	const messagesRef = collection(sessionRef, "messages");

	let showSettings = false;

	// --- COOKIE HELPERS ---
	function setCookie(name, value, days) {
		let expires = "";
		if (days) {
			let date = new Date();
			date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
			expires = "; expires=" + date.toUTCString();
		}
		document.cookie = name + "=" + (value || "") + expires + "; path=/";
	}

	function getCookie(name) {
		let nameEQ = name + "=";
		let ca = document.cookie.split(";");
		for (let i = 0; i < ca.length; i++) {
			let c = ca[i];
			while (c.charAt(0) == " ") c = c.substring(1, c.length);
			if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
		}
		return null;
	}

	// Read cookies synchronously on load
	const cookieAlias = getCookie(`gm_alias_${gid}`);
	const cookieVendor = getCookie(`gm_vendor_${gid}`);

	if (cookieAlias) alias = cookieAlias;
	if (cookieVendor) vendor = cookieVendor;

	function saveLocalSettings() {
		setCookie(`gm_alias_${gid}`, alias, 30);
		setCookie(`gm_vendor_${gid}`, vendor, 30);
		showSettings = false; // Close the panel after saving
	}

	checkForAlias(user.uid);

	async function checkForAlias(authUID) {
		const actSesSnap = await getDoc(doc(db, "users", authUID));

		if (actSesSnap.exists()) {
			let snapData = actSesSnap.data();
			// Only apply Firestore alias if a local cookie hasn't overridden it
			if (snapData.alias && !cookieAlias) {
				alias = snapData.alias;
			}
		} else {
			console.log("No such user!");
		}
	}

	// The dynamic subscription function
	function subscribeToMessages(currentLimit) {
		if (unsubscribe) unsubscribe();

		const q = query(
			messagesRef,
			orderBy("createdAt", "desc"),
			limit(currentLimit),
		);

		unsubscribe = onSnapshot(q, async (snapshot) => {
			let oldScrollHeight = chatContainer ? chatContainer.scrollHeight : 0;
			let isAtBottom = chatContainer
				? chatContainer.scrollHeight - chatContainer.scrollTop <=
					chatContainer.clientHeight + 50
				: true;

			// Reverse array so chronological order reads top-to-bottom
			messagesArray = snapshot.docs
				.map((doc) => ({
					id: doc.id,
					...doc.data(),
				}))
				.reverse();

			isLoadingMore = false;

			await tick();

			if (chatContainer) {
				if (currentLimit === 5 || isAtBottom) {
					chatContainer.scrollTo({
						top: chatContainer.scrollHeight,
						behavior: "smooth",
					});
				} else if (currentLimit > 5 && !isAtBottom) {
					let newScrollHeight = chatContainer.scrollHeight;
					chatContainer.scrollTop =
						chatContainer.scrollTop + (newScrollHeight - oldScrollHeight);
				}
			}
		});
	}

	// Initialize the first load
	subscribeToMessages(messageLimit);

	// Scroll handler to load older messages
	function handleScroll() {
		if (!chatContainer) return;

		if (chatContainer.scrollTop === 0 && !isLoadingMore) {
			if (messagesArray.length === messageLimit) {
				isLoadingMore = true;
				messageLimit += 10;
				subscribeToMessages(messageLimit);
			}
		}
	}

	// Wipe the GM's unread counter when the widget opens
	onMount(async () => {
		if (team.unreadPlayerMessages > 0) {
			await updateDoc(sessionRef, {
				unreadPlayerMessages: 0,
			});
		}
	});

	onDestroy(() => {
		if (unsubscribe) unsubscribe();
	});

	async function sendTextMessage() {
		if (!newMessage.trim()) return;

		const textToSend = newMessage;
		newMessage = ""; // Clear input immediately for better UX
		if (textInput) textInput.focus();

		await addDoc(messagesRef, {
			text: textToSend,
			senderId: "gm",
			senderName: alias ? alias : user.name ? user.name : "Obelisk",
			role: "admin",
			type: "text",
			createdAt: serverTimestamp(),
			...(vendor ? { vendor: vendor } : {}),
		});

		// Ping the player's notification counter
		await updateDoc(sessionRef, {
			unreadAdminMessages: increment(1),
		});
	}

	async function handleImageUpload(event) {
		const file = event.target.files[0];
		if (!file || !storage) return;

		isUploading = true;
		try {
			const filename = `${Date.now()}_${file.name}`;
			const storageRef = ref(
				storage,
				`sessions/${team.session.sid}/chat/${filename}`,
			);
			const snapshot = await uploadBytes(storageRef, file);
			const downloadURL = await getDownloadURL(snapshot.ref);

			await addDoc(messagesRef, {
				text: "Image uploaded",
				imageUrl: downloadURL,
				senderId: "gm",
				senderName: "Obelisk",
				role: "admin",
				type: "image",
				createdAt: serverTimestamp(),
			});

			await updateDoc(sessionRef, {
				unreadAdminMessages: increment(1),
			});
		} catch (error) {
			console.error("Image upload failed:", error);
			alert("Failed to upload image.");
		} finally {
			isUploading = false;
			fileInput.value = "";
		}
	}
</script>

<div class="gm-chat-widget">
	<!-- WIDGET HEADER -->
	<div class="widget-header">
		<div class="header-title">
			<i class="fa-solid fa-messages opacity-50"></i>
			<strong>{team.session.team_name}</strong>
		</div>
		<div class="header-actions">
			<button
				class="close-btn default px-2"
				on:click={() => (showSettings = !showSettings)}
				title="Chat Settings"
			>
				<i class="fa-solid fa-ellipsis-vertical"></i>
			</button>
			<button class="close-btn default" on:click={() => dispatch("close")}>
				<i class="fa-solid fa-times"></i>
			</button>
		</div>
	</div>

	{#if showSettings}
		<div class="settings-panel">
			<label class="settings-label">
				Local Alias for GID: {gid}
				<input
					class="default settings-input"
					type="text"
					bind:value={alias}
					placeholder="e.g. The Gamemaster"
				/>
			</label>
			<label class="settings-label">
				Local Vendor
				<input
					class="default settings-input"
					type="text"
					bind:value={vendor}
					placeholder="e.g. Obelisk Interactive"
				/>
			</label>
			<button class="default settings-save-btn" on:click={saveLocalSettings}>
				Save for 30 days
			</button>
		</div>
	{/if}

	<!-- MESSAGES AREA -->
	<div class="messages" bind:this={chatContainer} on:scroll={handleScroll}>
		{#if isLoadingMore}
			<div
				style="text-align: center; opacity: 0.6; font-size: 0.8rem; margin-bottom: 8px;"
			>
				Loading older messages...
			</div>
		{/if}

		{#each messagesArray as msg (msg.id)}
			<div
				class="msg-bubble"
				class:admin={msg.role === "admin"}
				class:player={msg.role !== "admin"}
			>
				<div class="msg-header">
					<strong>{msg.senderName}</strong>
				</div>

				{#if msg.type === "image"}
					<!-- svelte-ignore a11y-click-events-have-key-events -->
					<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
					<img
						src={msg.imageUrl}
						alt="Uploaded by {msg.senderName}"
						class="chat-img"
						on:click={() => (expandedImage = msg.imageUrl)}
					/>
				{:else}
					<div class="msg-text">{msg.text}</div>
				{/if}
				<small class="timestamp">
					<em>
						{#if msg.createdAt}
							{msg.createdAt
								.toDate()
								.toLocaleTimeString(`${lang}-${lang.toUpperCase()}`, {
									hour: "2-digit",
									minute: "2-digit",
								})}
						{:else}
							...
						{/if}
					</em>
				</small>
			</div>
		{/each}
	</div>

	<!-- INPUT AREA -->
	<div class="chat-input-area">
		{#if storage}
			<input
				type="file"
				accept="image/*"
				bind:this={fileInput}
				on:change={handleImageUpload}
				style="display: none;"
			/>
			<button
				class="icon-btn default"
				on:click={() => fileInput.click()}
				disabled={isUploading}
				title={ui.chat?.send_image || "Send Image"}
			>
				<i
					class="text-white fa-solid {isUploading
						? 'fa-circle-notch fa-spin'
						: 'fa-image'}"
				></i>
			</button>
		{/if}

		<input
			class="default"
			type="text"
			bind:this={textInput}
			bind:value={newMessage}
			on:keydown={(e) => e.key === "Enter" && sendTextMessage()}
			placeholder={ui.chat?.type_message || "Type a message..."}
			size="1"
		/>
		<button class="send-btn default" on:click={sendTextMessage}>
			<i class="fa-solid fa-paper-plane-top"></i>
		</button>
	</div>
</div>

<!-- FULLSCREEN IMAGE MODAL -->
{#if expandedImage}
	<!-- svelte-ignore a11y-click-events-have-key-events -->
	<!-- svelte-ignore a11y-no-static-element-interactions -->
	<div class="image-modal" on:click={() => (expandedImage = null)}>
		<img src={expandedImage} alt="Expanded view" />
	</div>
{/if}

<style>
	.gm-chat-widget {
		width: 320px;
		height: 450px;
		background: #f8fafc;
		border: 2px solid #00214d;
		/* border-radius: 8px 8px 0 0; */
		display: flex;
		flex-direction: column;
		box-shadow: 0 -4px 15px rgba(0, 0, 0, 0.2);
		overflow: hidden;
	}

	.widget-header {
		background: #00214d;
		color: white;
		padding: 10px 15px;
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.header-title {
		display: flex;
		gap: 8px;
		align-items: center;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.close-btn {
		background: none;
		border: none;
		color: white;
		cursor: pointer;
		font-size: 1.2rem;
		/* padding: 0; */
		/* margin: 0; */
	}

	.close-btn:hover {
		color: #fdda00;
	}

	.messages {
		flex: 1;
		overflow-y: auto;
		padding: 15px;
		display: flex;
		flex-direction: column;
		gap: 10px;
		background: #e2e8f0;
	}

	.msg-bubble {
		padding: 8px 12px;
		border-radius: 8px;
		max-width: 85%;
		font-size: 0.9rem;
		color: #1e293b;
	}

	/* In the GM Panel, Player messages are on the left */
	.msg-bubble.player {
		background: white;
		align-self: flex-start;
		border-left: 4px solid #00214d;
	}

	/* In the GM Panel, Admin messages are on the right */
	.msg-bubble.admin {
		background: #9fffed;
		align-self: flex-end;
		border-right: 4px solid #00e8bd;
	}

	.msg-header {
		font-size: 0.75rem;
		margin-bottom: 4px;
		opacity: 0.7;
		font-style: italic;
	}

	.timestamp {
		display: block;
		text-align: right;
		font-size: 0.7rem;
		opacity: 0.6;
		margin-top: 4px;
	}

	.chat-img {
		max-width: 100%;
		border-radius: 4px;
		margin-top: 4px;
		cursor: zoom-in;
	}

	.chat-input-area {
		display: flex;
		padding: 10px;
		background: white;
		border-top: 1px solid #cbd5e1;
		gap: 8px;
	}

	.chat-input-area input[type="text"] {
		flex: 1;
		padding: 8px;
		border: 1px solid #cbd5e1;
		border-radius: 4px;
		color: black;
	}

	.icon-btn,
	.send-btn {
		background: #00214d;
		color: white;
		border: none;
		padding: 8px 12px;
		border-radius: 4px;
		cursor: pointer;
	}

	.icon-btn:disabled {
		background: #94a3b8;
		cursor: not-allowed;
	}

	.image-modal {
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100dvh;
		background: rgba(0, 0, 0, 0.85);
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 99999;
		cursor: zoom-out;
		padding: 20px;
	}

	.image-modal img {
		max-width: 90%;
		max-height: 90%;
		object-fit: contain;
		border-radius: 8px;
	}

	.header-actions {
		display: flex;
		gap: 12px;
		align-items: center;
	}

	.settings-panel {
		background: #003a8b; /* A lighter blue to contrast with the deep header */
		color: white;
		padding: 15px;
		display: flex;
		flex-direction: column;
		gap: 10px;
		border-bottom: 2px solid #00214d;
	}

	.settings-label {
		display: flex;
		flex-direction: column;
		font-size: 0.8rem;
		gap: 4px;
		opacity: 0.9;
	}

	.settings-input {
		padding: 6px 8px;
		border-radius: 4px;
		border: 1px solid #9fffed;
		background: #00214d;
		color: white;
		font-size: 0.9rem;
	}

	.settings-save-btn {
		background: #00e8bd !important;
		color: #00214d !important;
		font-weight: bold;
		padding: 8px;
		margin-top: 5px;
		text-align: center;
		border-radius: 4px;
	}

	.settings-save-btn:hover {
		background: #9fffed !important;
	}

	/* --- MOBILE RESPONSIVENESS --- */
	@media (max-width: 768px) {
		.gm-chat-widget {
			/* Break out of the parent flex container */
			position: fixed;
			top: 0;
			left: 0;

			/* Take up the entire screen */
			width: 100vw;

			/* Use 'dvh' (Dynamic Viewport Height) so the mobile keyboard 
               doesn't push the input field off-screen */
			height: 100dvh;

			border: none;
			border-radius: 0;
			z-index: 99999;
		}

		.widget-header {
			/* Make the header slightly taller for easier tapping on mobile */
			padding: 15px;
		}

		.close-btn {
			/* Increase hit area for the close button */
			padding: 10px;
			margin: -10px;
		}
	}
</style>
